import { useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Button } from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import { PettyCashBillListHeader } from "./PettyCashBillListHeader"
import PettyCashBillAddEdit from "./PettyCashBillAddEdit"
import PettyCashBillView from "./PettyCashBillView"
import PettyCashTransactionAddEdit from "../PettyCashTransactionList/PettyCashTransactionAddEdit"
import { useListParams } from "hooks/useListParams"
import {
  usePettyCashBills,
  useDeletePettyCashBill,
} from "hooks/usePettyCashBills"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { formatUiDate } from "utils/formatDate"

const PettyCashBillList = () => {
  const { params, setPage, resetParams, updateParams } = useListParams({
    page: 1,
    search: "",
  })

  const [modal, setModal] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [topUpModal, setTopUpModal] = useState(false)
  const [bill, setBill] = useState(null)
  const [isEdit, setIsEdit] = useState(false)

  // Hooks
  const queryClient = useQueryClient()
  const { data: billsResponse, isLoading, refetch } = usePettyCashBills(params)
  const { mutateAsync: deleteBill } = useDeletePettyCashBill()

  const toggleModal = () => setModal(!modal)
  const toggleViewModal = () => setViewModal(!viewModal)
  const toggleTopUpModal = () => setTopUpModal(!topUpModal)

  const handleAddNewClick = () => {
    setBill(null)
    setIsEdit(false)
    toggleModal()
  }

  const handleTopUpClick = () => {
    toggleTopUpModal()
  }

  const handleEditClick = item => {
    setBill(item)
    setIsEdit(true)
    toggleModal()
  }

  const handleViewClick = item => {
    setBill(item)
    toggleViewModal()
  }

  const handleDeleteClick = item => {
    setBill(item)
    setDeleteModal(true)
  }

  const handleDelete = () => {
    if (!bill || !bill.id) return
    deleteBill(bill.id).then(() => {
      setDeleteModal(false)
      refetch()
    })
  }

  const { isLoading: optionLoading, raw: rawData } = useFilteredVehicleOptions()

  // Options
  const paymentModesData = useMemo(
    () => rawData && rawData.petty_cash && rawData.petty_cash.payment_modes || [],
    [rawData]
  )
  const paymentForsData = useMemo(
    () => rawData && rawData.petty_cash && rawData.petty_cash.payment_fors || [],
    [rawData]
  )
  const paymentFromsData = useMemo(
    () => rawData && rawData.petty_cash && rawData.petty_cash.payment_froms || [],
    [rawData]
  )
  const employeesData = useMemo(() => rawData && rawData.employees || [], [rawData])
  const topUpTransaction = useMemo(() => ({ payment_type: "cashin" }), [])

  const columns = useMemo(
    () => [
      { header: "Bill No", accessorKey: "bill_no" },
      {
        header: "Bill Date",
        accessorKey: "bill_date",
        cell: ({ row }) => formatUiDate(row.original.bill_date),
      },
      { header: "Total Amount", accessorKey: "total_amount" },
      { header: "Paid", accessorKey: "paid_amount" },
      { header: "Due", accessorKey: "due_amount" },
      { header: "Status", accessorKey: "status" },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="hstack gap-1">
            <Button
              color="info"
              size="sm"
              onClick={() => handleViewClick(row.original)}
            >
              <i className="mdi mdi-eye" />
            </Button>
            <Button
              color="success"
              size="sm"
              onClick={() => handleEditClick(row.original)}
            >
              <i className="mdi mdi-pencil-outline" />
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => handleDeleteClick(row.original)}
            >
              <i className="mdi mdi-delete-outline" />
            </Button>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <>
      <PettyCashBillListHeader
        handleAddNewClick={handleAddNewClick}
        handleTopUpClick={handleTopUpClick}
        handleRefresh={refetch}
        total={billsResponse && billsResponse.data && billsResponse.data.total || 0}
        isRefetching={false}
      />

      <TableContainer
        columns={columns}
        data={billsResponse && billsResponse.data && billsResponse.data.data || []}
        isPagination={true}
        paginationInfo={billsResponse && billsResponse.data}
        onPageChange={setPage}
        loading={isLoading}
      />

      <PettyCashBillAddEdit
        modal={modal}
        toggleModal={toggleModal}
        isEdit={isEdit}
        bill={bill}
        paymentModesData={paymentModesData}
        paymentForsData={paymentForsData}
        paymentFromsData={paymentFromsData}
        submittedData={employeesData}
      />

      <PettyCashBillView
        modal={viewModal}
        toggleModal={toggleViewModal}
        bill={bill}
      />

      <PettyCashTransactionAddEdit
        modal={topUpModal}
        toggleModal={toggleTopUpModal}
        isEdit={false}
        transaction={topUpTransaction}
        optionLoading={optionLoading}
        paymentModesData={paymentModesData}
        paymentForsData={paymentForsData}
        paymentFromsData={paymentFromsData}
        receiversData={employeesData}
        onSuccess={() => queryClient.invalidateQueries(["petty-cash-balance"])}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />
    </>
  )
}

export default PettyCashBillList
